/**
 * How a listener usually shows up. Separate from conversation topics.
 * Not therapy, counseling, crisis support, or professional expertise.
 *
 * Stored on the Listening profile as publicData.listeningStyles (array of ids).
 */

export const LISTENING_STYLE_IDS = [
  'easygoing',
  'warm-encouraging',
  'detail-oriented',
  'analytical',
  'direct',
  'challenging',
  'reflective',
  'lighthearted',
  'calm-steady',
  'practical',
];

export const LISTENING_STYLE_LABELS = {
  easygoing: 'Easygoing',
  'warm-encouraging': 'Warm and encouraging',
  'detail-oriented': 'Detail-oriented',
  analytical: 'Analytical',
  direct: 'Direct',
  challenging: 'Challenging',
  reflective: 'Reflective',
  lighthearted: 'Lighthearted',
  'calm-steady': 'Calm and steady',
  practical: 'Practical',
};

export const LISTENING_STYLES = LISTENING_STYLE_IDS.map(id => ({
  id,
  label: LISTENING_STYLE_LABELS[id],
  messageId: `ListeningStyles.${id}`,
}));

export const LISTENING_STYLES_PARAM = 'listeningStyles';

export const isListeningStyleId = value => LISTENING_STYLE_IDS.includes(value);

export const normalizeListeningStyles = value => {
  const list = Array.isArray(value)
    ? value
    : typeof value === 'string'
    ? value.split(',')
    : [];
  const seen = new Set();
  return list
    .map(item => String(item || '').trim())
    .filter(id => {
      if (!isListeningStyleId(id) || seen.has(id)) {
        return false;
      }
      seen.add(id);
      return true;
    });
};

export const parseListeningStylesParam = value => normalizeListeningStyles(value);

export const listeningStylesFromListing = listing =>
  normalizeListeningStyles(
    listing?.attributes?.publicData?.listeningStyles || listing?.publicData?.listeningStyles
  );

export const listingOffersListeningStyles = (listing, wanted) => {
  const need = normalizeListeningStyles(wanted);
  if (need.length === 0) {
    return true;
  }
  const have = listeningStylesFromListing(listing);
  if (have.length === 0) {
    return true;
  }
  return need.some(id => have.includes(id));
};

export const filterListingsByListeningStyles = (listings = [], wanted) => {
  const need = normalizeListeningStyles(wanted);
  if (need.length === 0) {
    return listings;
  }
  return listings.filter(listing => listingOffersListeningStyles(listing, need));
};

export const listeningStyleSearchHaystack = styles => {
  const ids = normalizeListeningStyles(styles);
  const labels = ids.map(id => LISTENING_STYLE_LABELS[id] || id);
  return [...ids, ...labels].filter(Boolean).join(' ');
};

export const listeningStyleChipLabel = id => {
  if (!isListeningStyleId(id)) {
    return '';
  }
  return LISTENING_STYLE_LABELS[id] || id;
};
